import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-100 text-blue-700",
  seen: "bg-indigo-100 text-indigo-700",
  in_review: "bg-amber-100 text-amber-700",
  negotiation: "bg-purple-100 text-purple-700",
  accepted: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  expired: "bg-slate-100 text-slate-500",
  cancelled: "bg-slate-100 text-slate-500",
};

export default function Proposals() {
  const { t } = useTranslation();
  const [data, setData] = useState({ sent: [], received: [] });

  const statusLabels = useMemo(() => ({
    draft: t("proposals.status_draft"),
    sent: t("proposals.status_sent"),
    seen: t("proposals.status_seen"),
    in_review: t("proposals.status_in_review"),
    negotiation: t("proposals.status_negotiation"),
    accepted: t("proposals.status_accepted"),
    rejected: t("proposals.status_rejected"),
    expired: t("proposals.status_expired"),
    cancelled: t("proposals.status_cancelled"),
  }), [t]);

  const load = async () => {
    const { data } = await api.get("/api/proposals");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/api/proposals/${id}/status`, { status });
      toast.success(t("proposals.toast_updated"));
      load();
    } catch (e) { toast.error(t("proposals.toast_update_failed")); }
  };

  return (
    <DashboardLayout title={t("proposals.title")} subtitle={t("proposals.subtitle")}>
      <Tabs defaultValue="received">
        <TabsList className="mb-6 bg-slate-100 rounded-full">
          <TabsTrigger value="received" data-testid="tab-received">{t("proposals.tab_received", { count: data.received.length })}</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">{t("proposals.tab_sent", { count: data.sent.length })}</TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="space-y-3">
          {data.received.length === 0 ? (
            <div className="kpi-card p-8 text-center text-slate-500">{t("proposals.empty_received")}</div>
          ) : data.received.map((p) => (
            <ProposalRow key={p.id} p={p} onStatus={updateStatus} isReceiver statusLabels={statusLabels} />
          ))}
        </TabsContent>
        <TabsContent value="sent" className="space-y-3">
          {data.sent.length === 0 ? (
            <div className="kpi-card p-8 text-center text-slate-500">{t("proposals.empty_sent")}</div>
          ) : data.sent.map((p) => (
            <ProposalRow key={p.id} p={p} onStatus={updateStatus} statusLabels={statusLabels} />
          ))}
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function ProposalRow({ p, onStatus, isReceiver, statusLabels }) {
  const { t } = useTranslation();
  return (
    <div className="kpi-card p-5" data-testid={`proposal-${p.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-display text-xl text-petroleum">{p.description?.slice(0, 60) || t("proposals.proposal_default_title")}</h3>
            <Badge className={STATUS_COLORS[p.status] || ""}>{statusLabels[p.status] || p.status}</Badge>
          </div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-slate-500">
            {p.modality && <span>{t("proposals.modality_prefix")} <span className="text-petroleum">{p.modality}</span></span>}
            {p.requested_amount && <span>{t("proposals.requested_prefix")} <span className="text-petroleum">{p.currency} {p.requested_amount.toLocaleString()}</span></span>}
            {p.offered_amount && <span>{t("proposals.offered_prefix")} <span className="text-emerald-600">{p.currency} {p.offered_amount.toLocaleString()}</span></span>}
            {p.valid_until && <span>{t("proposals.expires_prefix", { date: p.valid_until })}</span>}
          </div>
        </div>
        {isReceiver && p.status !== "accepted" && p.status !== "rejected" && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onStatus(p.id, "accepted")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`button-accept-${p.id}`}>{t("proposals.accept")}</Button>
            <Button size="sm" variant="outline" onClick={() => onStatus(p.id, "rejected")} data-testid={`button-reject-${p.id}`}>{t("proposals.reject")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
